import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../context/AuthContext';
import productService from '../services/productService';
import { FaArrowLeft, FaEdit, FaPlus, FaTrash } from 'react-icons/fa';
import '../css/ProductManager.css';

const ProductManager = () => {
  const { user } = useContext(AuthContext);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isAddingNewProduct, setIsAddingNewProduct] = useState(false);
  const [newProduct, setNewProduct] = useState({
    id: '',  // Codice padre
    category: '',
    sottocategoria: '',
    ambiente: '',
    name: '',
    descrizione: '',
    colore_LED: [{ value: '', code: '' }],
    ottica: [{ value: '', code: '' }],
    input_voltage: '',
    finitura: [{ value: '', code: '' }],
    power: [{ value: '', code: '' }],
    lunghezza: '',  // Lunghezza da inserire
    codice_lunghezza: '',  // Codice relativo alla lunghezza
    codice: '',     // Codice completo da generare
    lumen: '',
    dimmer: '',
    size: '',
    ipRating: '',
    price: 0,
    sale: false,
    isNew: false,
    sale_price: 0,
    image: '',
    datasheet: '',
    fotometria: '',
    techDrawing: '',
    active: true
  });
  const [loading, setLoading] = useState(false);

  // Carica i prodotti dal backend
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const productsData = await productService.getProtectedProducts(user.token);
        setProducts(productsData); // Aggiorna lo stato con i prodotti ricevuti
      } catch (error) {
        console.error('Errore nel recupero dei prodotti:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user && user.role === 'admin') {
      fetchProducts();
    }
  }, [user]);

  const handleBack = () => {
    setSelectedProduct(null);
    setIsAddingNewProduct(false);
    resetNewProduct();
  };

  const resetNewProduct = () => {
    setNewProduct({
      id: '',
      category: '',
      sottocategoria: '',
      ambiente: '',
      name: '',
      descrizione: '',
      colore_LED: [{ value: '', code: '' }],
      ottica: [{ value: '', code: '' }],
      input_voltage: '',
      finitura: [{ value: '', code: '' }],
      power: [{ value: '', code: '' }],
      lunghezza: '',
      codice_lunghezza: '', // Codice relativo alla lunghezza
      codice: '', // Codice completo da generare
      lumen: '',
      dimmer: '',
      size: '',
      ipRating: '',
      price: 0,
      sale: false,
      isNew: false,
      sale_price: 0,
      image: '',
      datasheet: '',
      fotometria: '',
      techDrawing: '',
      active: true
    });
  };

  const handleAddNewProduct = () => {
    resetNewProduct();
    setSelectedProduct(null);
    setIsAddingNewProduct(true);
  };

  useEffect(() => {
    if (selectedProduct) {
      setNewProduct({ ...selectedProduct });
      setIsAddingNewProduct(false);
    } else if (!isAddingNewProduct) {
      resetNewProduct();
    }
  }, [selectedProduct, isAddingNewProduct]);

  const handleSaveProduct = async () => {
    setLoading(true);
    try {
      // Genera il codice completo basato sull'ID e, se presente, la lunghezza
      let codiceCompleto = newProduct.id;
      if (newProduct.lunghezza) {
        codiceCompleto = `${newProduct.id}-${newProduct.codice_lunghezza}`;
      }

      // Aggiorna il nuovo prodotto con il codice completo
      const updatedProduct = {
        ...newProduct,
        codice: codiceCompleto,
        price: parseFloat(newProduct.price), // Assicurati che il prezzo sia un numero
        sale_price: parseFloat(newProduct.sale_price), // Assicurati che il prezzo in sconto sia un numero
        isNew: !!newProduct.isNew,
      };

      if (selectedProduct) {
        // Aggiorna prodotto esistente
        await productService.updateProduct(selectedProduct.id, updatedProduct, user.token);
      } else {
        // Aggiungi nuovo prodotto
        await productService.createProduct(updatedProduct, user.token);
      }

      // Aggiorna la lista dei prodotti
      const updatedProducts = await productService.getProtectedProducts(user.token);
      setProducts(updatedProducts);
      handleBack();
    } catch (error) {
      console.error('Errore durante il salvataggio del prodotto:', error);
    } finally {
      setLoading(false);
    }
  };

  // Funzione per eliminare un prodotto
  const handleDeleteProduct = async (productId) => {
    if (window.confirm('Sei sicuro di voler eliminare questo prodotto?')) {
      setLoading(true);
      try {
        await productService.deleteProduct(productId, user.token);
        setProducts(products.filter(p => p.id !== productId));
      } catch (error) {
        console.error("Errore durante l'eliminazione del prodotto:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="product-manager-container">
      <h2>Gestione Prodotti</h2>
      {loading && <div className="loading-spinner">Caricamento...</div>}
      {!selectedProduct && !isAddingNewProduct ? (
        <div className="section">
          <h4>Prodotti Disponibili:</h4>
          {products.length === 0 ? (
            <p>Nessun prodotto disponibile.</p>
          ) : (
            <table className="product-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nome</th>
                  <th>Categoria</th>
                  <th>Prezzo</th>
                  <th>Prezzo in Sconto</th>
                  <th>In Offerta</th>
                  <th>Novita</th>
                  <th>Attivo</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {products.map(product => (
                  <tr key={product.id}>
                    <td>{product.id}</td>
                    <td>{product.name}</td>
                    <td>{product.category}</td>
                    <td>€{product.price}</td>
                    <td>{product.sale ? `€${product.sale_price}` : 'N/A'}</td>
                    <td>{product.sale ? 'Sì' : 'No'}</td>
                    <td>{product.isNew ? 'Sì' : 'No'}</td>
                    <td>{product.active ? 'Sì' : 'No'}</td>
                    <td>
                      <FaEdit onClick={() => setSelectedProduct(product)} style={{ cursor: 'pointer', marginRight: '10px' }} title="Modifica Prodotto" />
                      <FaTrash onClick={() => handleDeleteProduct(product.id)} style={{ cursor: 'pointer' }} title="Elimina Prodotto" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <button onClick={handleAddNewProduct} className="add-button">
            <FaPlus /> Aggiungi Nuovo Prodotto
          </button>
        </div>
      ) : (
        <div className="section">
          <button onClick={handleBack} className="back-button">
            <FaArrowLeft /> Indietro
          </button>
          <h4>{selectedProduct ? 'Modifica Prodotto' : 'Aggiungi Nuovo Prodotto'}</h4>

          {/* Form per aggiungere o modificare il prodotto */}
          <div className="product-form">
            {/* Informazioni Generali */}
            <h5>Informazioni Generali</h5>
            <label>ID:</label>
            <input className="compact-input" type="text" value={newProduct.id} onChange={(e) => setNewProduct({ ...newProduct, id: e.target.value })} />

            <label>Nome:</label>
            <input className="compact-input" type="text" value={newProduct.name} onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })} />

            <label>Categoria:</label>
            <input className="compact-input" type="text" value={newProduct.category} onChange={(e) => setNewProduct({ ...newProduct, category: e.target.value })} />

            <label>Sottocategoria:</label>
            <input className="compact-input" type="text" value={newProduct.sottocategoria} onChange={(e) => setNewProduct({ ...newProduct, sottocategoria: e.target.value })} />

            <label>Ambiente:</label>
            <input className="compact-input" type="text" value={newProduct.ambiente} onChange={(e) => setNewProduct({ ...newProduct, ambiente: e.target.value })} />

            <label>Descrizione:</label>
            <textarea className="compact-textarea" value={newProduct.descrizione} onChange={(e) => setNewProduct({ ...newProduct, descrizione: e.target.value })} />

            {/* Lunghezza e Codice Lunghezza */}
            <label>Lunghezza (se applicabile):</label>
            <input
              className="compact-input"
              type="text"
              value={newProduct.lunghezza}
              onChange={(e) => setNewProduct({ ...newProduct, lunghezza: e.target.value })}
            />
            {newProduct.lunghezza && (
              <>
                <label>Codice Lunghezza:</label>
                <input
                  className="compact-input"
                  type="text"
                  value={newProduct.codice_lunghezza}
                  onChange={(e) => setNewProduct({ ...newProduct, codice_lunghezza: e.target.value })}
                />
              </>
            )}

            {/* Altre specifiche tecniche e caricamenti di file */}
            <h5>Specifiche Tecniche</h5>
            {/* ... continua con Colore LED, Ottica, Finitura, etc. ... */}
            
            <h5>Prezzi e Immagini</h5>
            <label>Prezzo:</label>
            <input className="compact-input" type="number" value={newProduct.price} onChange={(e) => setNewProduct({ ...newProduct, price: parseFloat(e.target.value) })} />

            <label>Prezzo in Sconto:</label>
            <input className="compact-input" type="number" value={newProduct.sale_price} onChange={(e) => setNewProduct({ ...newProduct, sale_price: parseFloat(e.target.value) })} />

            {/* Stato del prodotto */}
            <h5>Stato del Prodotto</h5>
            <label>In Offerta:</label>
            <input type="checkbox" checked={newProduct.sale} onChange={(e) => setNewProduct({ ...newProduct, sale: e.target.checked })} />

            <label>Novita:</label>
            <input type="checkbox" checked={!!newProduct.isNew} onChange={(e) => setNewProduct({ ...newProduct, isNew: e.target.checked })} />

            <label>Prodotto Attivo:</label>
            <input type="checkbox" checked={newProduct.active} onChange={(e) => setNewProduct({ ...newProduct, active: e.target.checked })} />

            <button onClick={handleSaveProduct} className="save-button compact-button">
              {selectedProduct ? 'Salva Modifiche' : 'Aggiungi Prodotto'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductManager;
